import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { notifications } from '@mantine/notifications';
import type { Department } from '@/types/models';

interface DepartmentFormData {
  name: string;
}

// Query hooks
export function useDepartments(organizationId: string | undefined) {
  return useQuery({
    queryKey: ['departments', organizationId],
    queryFn: async () => {
      if (!organizationId) throw new Error('Organization ID is required');

      const { data, error } = await supabase
        .from('departments')
        .select('*')
        .eq('organization_id', organizationId)
        .order('name', { ascending: true });

      if (error) throw error;
      return data as Department[];
    },
    enabled: !!organizationId,
  });
}

export function useDepartment(departmentId: string | undefined) {
  return useQuery({
    queryKey: ['departments', departmentId],
    queryFn: async () => {
      if (!departmentId) throw new Error('Department ID is required');

      const { data, error } = await supabase
        .from('departments')
        .select('*')
        .eq('id', departmentId)
        .single();

      if (error) throw error;
      return data as Department;
    },
    enabled: !!departmentId,
  });
}

// Mutation hooks
export function useCreateDepartment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: DepartmentFormData & { organization_id: string }) => {
      const { error, data: newDepartment } = await supabase
        .from('departments')
        .insert([data])
        .select()
        .single();

      if (error) throw error;
      return newDepartment;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments'] });
      notifications.show({
        title: 'Success',
        message: 'Department created successfully',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to create department',
        color: 'red',
      });
    },
  });
}

export function useUpdateDepartment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<DepartmentFormData> }) => {
      const { error, data: updatedDepartment } = await supabase
        .from('departments')
        .update(data)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return updatedDepartment;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments'] });
      notifications.show({
        title: 'Success',
        message: 'Department updated successfully',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to update department',
        color: 'red',
      });
    },
  });
}

export function useDeleteDepartment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (departmentId: string) => {
      const { error } = await supabase.from('departments').delete().eq('id', departmentId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments'] });
      notifications.show({
        title: 'Success',
        message: 'Department deleted successfully',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      notifications.show({
        title: 'Error',
        message: error.message || 'Failed to delete department',
        color: 'red',
      });
    },
  });
}
